export enum GlobalSearchEntityTypes {
  COMPANIES = "companies",
  PARTNERS = "partners",
  DEALS = "deals",
  LEADS = "leads",
  PEOPLE = "people",
  // Not a selectable filter tab — partner contacts are merged into the
  // People tab/section, but the API still returns this as the raw entityType.
  PARTNERS_CONTACTS = "partners_contacts",
  CONTRACTS = "contracts"
}

export enum GlobalSearchFilterTypes {
  ALL = "all",
  COMPANIES = GlobalSearchEntityTypes.COMPANIES,
  PARTNERS = GlobalSearchEntityTypes.PARTNERS,
  DEALS = GlobalSearchEntityTypes.DEALS,
  LEADS = GlobalSearchEntityTypes.LEADS,
  PEOPLE = GlobalSearchEntityTypes.PEOPLE,
  CONTRACTS = GlobalSearchEntityTypes.CONTRACTS
}

// Filter tabs that fetch/display more than their own like-named entity type.
// Single source of truth for both the API request scope (useGlobalSearchAPI)
// and the client-side result filter (useGlobalSearch) so they can't drift.
export const FILTER_TO_ENTITY_TYPES: Partial<
  Record<GlobalSearchFilterTypes, GlobalSearchEntityTypes[]>
> = {
  [GlobalSearchFilterTypes.PEOPLE]: [
    GlobalSearchEntityTypes.PEOPLE,
    GlobalSearchEntityTypes.PARTNERS_CONTACTS
  ]
};

export enum GlobalSearchStates {
  IDLE = "idle",
  LOADING = "loading",
  EMPTY = "empty",
  RESULTS = "results"
}

export type GlobalSearchSortBy = "relevance" | "name" | "date";

/**
 * Comma-separated fields the query is matched against. Omitted means the API
 * matches every indexed string field, which is the default for app-wide search.
 * The API keeps this field order and silently drops fields absent from a given
 * entity's schema, so a list only has to name the fields it wants where they
 * exist — but an entity with none of them is skipped entirely.
 */
export const QUERY_BY_NAME_FIELDS = "name,display_name";

export interface GlobalSearchRequest {
  query: string;
  entityTypes?: GlobalSearchEntityTypes[] | null;
  page?: number;
  pageSize?: number;
  sortBy?: GlobalSearchSortBy;
  includeLookupTables?: boolean;
  /** Fields to return in each result — not the fields searched. See queryBy. */
  includeFields?: string;
  queryBy?: string;
}

export interface GlobalSearchResult {
  entityType: GlobalSearchEntityTypes;
  id: string;
  name: string;
  highlights: {
    name?: string;
    [key: string]: string | undefined;
  };
  metadata: Record<string, any>;
}

export interface GlobalSearchResponse {
  results: GlobalSearchResult[];
  total: number;
  queryTimeMs: number;
  page: number;
  pageSize: number;
  facets?: any;
}

export interface RecentSearchItem {
  id: string;
  query: string;
  entityType?: GlobalSearchEntityTypes;
  resultId?: string;
  resultEntityType?: GlobalSearchEntityTypes;
  resultName?: string;
  searchedAt: string;
}

export interface RecentSearchResponse {
  recentSearches: RecentSearchItem[];
}

export interface CreateRecentSearchRequest {
  query: string;
  entityType?: GlobalSearchEntityTypes;
  resultId: string;
  resultEntityType: GlobalSearchEntityTypes;
  resultName: string;
}
